<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

const props = defineProps<{ slices: DonutSlice[] }>();

const canvas = ref<HTMLCanvasElement | null>(null);

function drawDonut() {
  const el = canvas.value;
  if (!el) return;
  const ctx = el.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const S = el.offsetWidth;
  el.width = S * dpr;
  el.height = S * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, S, S);

  const cx = S / 2,
    cy = S / 2;
  const R = S * 0.42;
  const r = S * 0.26;
  const total = props.slices.reduce((a, s) => a + s.value, 0);
  let startAngle = -Math.PI / 2;

  props.slices.forEach((slice) => {
    const angle = (slice.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, R, startAngle, startAngle + angle);
    ctx.closePath();
    ctx.fillStyle = slice.color;
    ctx.fill();
    startAngle += angle;
  });

  // Hole
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = isDark ? '#0a0c0d' : '#ffffff';
  ctx.fill();

  // Center label
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const pct = props.slices.length
    ? Math.round((props.slices[0].value / total) * 100)
    : 0;
  ctx.fillStyle = isDark ? '#e2e4e5' : '#191c1d';
  ctx.font = `800 ${S * 0.115}px system-ui, sans-serif`;
  ctx.fillText(`${pct}%`, cx, cy - S * 0.03);
  ctx.font = `600 ${S * 0.065}px system-ui, sans-serif`;
  ctx.fillStyle = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
  ctx.fillText(props.slices[0]?.label ?? '', cx, cy + S * 0.07);
}

let ro: ResizeObserver | null = null;
onMounted(() => {
  drawDonut();
  ro = new ResizeObserver(() => drawDonut());
  if (canvas.value?.parentElement) ro.observe(canvas.value.parentElement);
});
onUnmounted(() => ro?.disconnect());
watch(() => props.slices, drawDonut, { deep: true });
</script>

<template>
  <div class="donut-wrap">
    <div class="donut-chart">
      <canvas ref="canvas" class="donut-canvas" />
    </div>
    <ul class="donut-legend">
      <li v-for="s in slices" :key="s.label" class="donut-legend__item">
        <span class="donut-legend__dot" :style="{ background: s.color }" />
        <span class="donut-legend__label">{{ s.label }}</span>
        <span class="donut-legend__pct">{{ s.value }}%</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.donut-wrap {
  display: flex;
  align-items: center;
  gap: 2rem;
}
.donut-chart {
  width: 140px;
  height: 140px;
  min-width: 140px;
}
.donut-canvas {
  width: 100%;
  height: 100%;
  display: block;
}
.donut-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  flex: 1;
}
.donut-legend__item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.donut-legend__dot {
  width: 0.625rem;
  height: 0.625rem;
  min-width: 0.625rem;
  border-radius: 50%;
}
.donut-legend__label {
  flex: 1;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.donut-legend__pct {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
</style>
